import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { useToast } from "../../../../../mantine/notifications/useToast";
import type { VollmachtDetail } from "../../../../../utils/backend-types";
import { getErrorText } from "../../../../../utils/get-error-text";
import { MarkDoneActionButton } from "../../../components/MarkDoneActionButton";
import { AssignmentType } from "../../OnboardingWizard.constants";
import { DownloadTemplateButton } from "./Buttons/DownloadTemplateButton";
import { UploadVollmachtButton } from "./Buttons/UploadVollmachtButton";
import { useDownloadVollmachtMsb } from "./hooks/useDownloadVollmachtMsb";
import { useUploadVollmachtMsb } from "./hooks/useUploadVollmachtMsb";
import { clearFileInput, VollmachtSection } from "./VollmachtSection";

interface VollmachtMsbProps {
  siteId: number;
  updateSiteWizard: (type: AssignmentType) => Promise<void>;
  phase: VollmachtDetail;
  isDisabled?: boolean;
}

export const vollmachtMsbId = "vollmacht-msb";

function VollmachtMsb({
  siteId,
  updateSiteWizard,
  phase,
  isDisabled
}: VollmachtMsbProps) {
  const { showErrorToast, showToast } = useToast();
  const { uploadVollmachtMsbMutation } = useUploadVollmachtMsb(siteId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { missingData, done, hasMissingDocuments } = phase;

  const hasMissingData = missingData.length > 0;

  const { generateAndDownloadVollmachtMsbMutation } =
    useDownloadVollmachtMsb(siteId);

  const submitMutation = useMutation({
    mutationFn: () => updateSiteWizard(AssignmentType.VollmachtMsb)
  });

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    uploadVollmachtMsbMutation.mutate(
      { files: Array.from(files) },
      {
        onSuccess: () => {
          showToast({
            type: "success",
            message: "Die Vollmacht wurde erfolgreich hochgeladen."
          });
        },
        onSettled: () => clearFileInput(fileInputRef),
        onError: (error) => showErrorToast(getErrorText(error))
      }
    );
  }

  return (
    <VollmachtSection
      actions={
        <>
          <DownloadTemplateButton
            onClick={() => generateAndDownloadVollmachtMsbMutation.mutate()}
          />
          <UploadVollmachtButton
            onClick={() => fileInputRef.current?.click()}
          />
          {!hasMissingDocuments && (
            <MarkDoneActionButton
              disabled={submitMutation.isPending}
              mb="xs"
              mt={0}
              onClick={() => submitMutation.mutate()}
            />
          )}
        </>
      }
      done={done}
      fileInputRef={fileInputRef}
      hasMissingData={hasMissingData}
      isDisabled={isDisabled}
      isLoading={
        generateAndDownloadVollmachtMsbMutation.isPending ||
        uploadVollmachtMsbMutation.isPending
      }
      missingDataRoute={vollmachtMsbId}
      objectName="Messstellenbetreiber"
      onFileChange={handleFileChange}
    />
  );
}

export { VollmachtMsb };
